import { type ThemeConfig, type ThemeMode } from "@/shared/theme/lib/types";

const DENSITY_SCALE = {
  comfortable: "1",
  compact: "0.85",
} as const;

const BORDER_TOKENS = {
  subtle: {
    light: { border: "oklch(0.94 0.003 286.32)", input: "oklch(0.94 0.003 286.32)" },
    dark: { border: "oklch(1 0 0 / 8%)", input: "oklch(1 0 0 / 12%)" },
  },
  normal: {
    light: { border: "oklch(0.92 0.004 286.32)", input: "oklch(0.92 0.004 286.32)" },
    dark: { border: "oklch(1 0 0 / 10%)", input: "oklch(1 0 0 / 15%)" },
  },
  strong: {
    light: { border: "oklch(0.85 0.006 286.32)", input: "oklch(0.85 0.006 286.32)" },
    dark: { border: "oklch(1 0 0 / 20%)", input: "oklch(1 0 0 / 25%)" },
  },
} as const;

function sanitizeHexColor(value: string, fallback: string): string {
  const trimmed = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed) ? trimmed : fallback;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.replace("#", "");
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : normalized;

  if (value.length !== 6) {
    return null;
  }

  const parsed = Number.parseInt(value, 16);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
}

function pickForegroundFromHex(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return "#ffffff";
  }

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.6 ? "#111827" : "#ffffff";
}

/**
 * Keep the browser/PWA status bar (`<meta name="theme-color">`) identical to the dual header's
 * background by reading the resolved `--header` token. Derives it from the real CSS variable rather
 * than duplicating hex values, so the status bar and header can never drift apart (and any future
 * header theming carries through automatically). No-op on platforms that ignore `theme-color` (iOS PWA).
 */
function syncStatusBarColorToHeader(root: HTMLElement): void {
  if (typeof window === "undefined") return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const headerColor = window.getComputedStyle(root).getPropertyValue("--header").trim();
  if (headerColor) {
    meta.setAttribute("content", headerColor);
  }
}

export function getResolvedMode(mode: ThemeMode): "light" | "dark" {
  if (mode === "light" || mode === "dark") {
    return mode;
  }
  if (typeof window === "undefined") {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyTheme(config: ThemeConfig): void {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  const resolvedMode = getResolvedMode(config.mode);

  root.classList.toggle("dark", resolvedMode === "dark");
  root.dataset.themeMode = config.mode;

  const primary = sanitizeHexColor(config.primary, "#059669");
  const accent = sanitizeHexColor(config.accent, "#d1fae5");
  const radius = Math.min(1, Math.max(0.4, config.radius));
  const borderTokens = BORDER_TOKENS[config.border][resolvedMode];

  root.style.setProperty("--primary", primary);
  root.style.setProperty("--primary-foreground", pickForegroundFromHex(primary));
  root.style.setProperty("--accent", accent);
  root.style.setProperty("--accent-foreground", pickForegroundFromHex(accent));
  root.style.setProperty("--radius", `${radius.toFixed(3)}rem`);
  root.style.setProperty("--density-scale", DENSITY_SCALE[config.density]);
  root.style.setProperty("--border", borderTokens.border);
  root.style.setProperty("--input", borderTokens.input);

  // Status bar (theme-color) must always equal the dual header's background (`--header`).
  syncStatusBarColorToHeader(root);
}
